import { useState, useEffect } from 'react';

interface AddressData {
  fullName: string;
  phone: string;
  address: string;
  city: string;
  pincode: string;
  lat?: number;
  lng?: number;
}

interface EditAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (address: AddressData) => Promise<void>;
  currentAddress?: AddressData | null;
}

export default function EditAddressModal({ isOpen, onClose, onSave, currentAddress }: EditAddressModalProps) {
  const [form, setForm] = useState<AddressData>({
    fullName: '',
    phone: '',
    address: '',
    city: '',
    pincode: '',
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<Partial<AddressData>>({});
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationStatus, setLocationStatus] = useState('');

  useEffect(() => {
    if (isOpen && currentAddress) {
      setForm({
        fullName: currentAddress.fullName || '',
        phone: currentAddress.phone || '',
        address: currentAddress.address || '',
        city: currentAddress.city || '',
        pincode: currentAddress.pincode || '',
        lat: currentAddress.lat,
        lng: currentAddress.lng,
      });
    }
    if (isOpen) {
      setErrors({});
      setSuccess(false);
    }
  }, [isOpen, currentAddress]);

  const validate = (): boolean => {
    const newErrors: Partial<AddressData> = {};
    if (!form.fullName.trim()) newErrors.fullName = 'Naam zaroor daalo';
    if (!form.phone.trim() || form.phone.replace(/\D/g, '').length < 10) newErrors.phone = 'Valid phone number daalo';
    if (!form.address.trim()) newErrors.address = 'Address zaroor daalo';
    if (!form.city.trim()) newErrors.city = 'City zaroor daalo';
    if (!form.pincode.trim() || form.pincode.replace(/\D/g, '').length !== 6) newErrors.pincode = 'Valid 6-digit pincode daalo';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus('GPS is not supported on this device.');
      return;
    }
    setGettingLocation(true);
    setLocationStatus('Getting your location...');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(prev => ({ ...prev, lat: pos.coords.latitude, lng: pos.coords.longitude }));
        setGettingLocation(false);
        setLocationStatus('Location captured!');
        setTimeout(() => setLocationStatus(''), 2500);
      },
      () => {
        setGettingLocation(false);
        setLocationStatus('Could not get location. Please allow location access.');
        setTimeout(() => setLocationStatus(''), 3000);
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave(form);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1500);
    } catch {
      // error handled by parent
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof AddressData, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4">
      <div className="bg-white dark:bg-slate-900 w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center">
              <i className="ri-map-pin-2-line text-orange-500 text-base"></i>
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-white">Edit Delivery Address</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Apna address update karo</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Success State */}
        {success ? (
          <div className="flex flex-col items-center justify-center py-14 gap-4 px-6">
            <div className="w-16 h-16 rounded-full bg-green-50 dark:bg-green-900/30 flex items-center justify-center">
              <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
            </div>
            <div className="text-center">
              <p className="text-gray-900 dark:text-white font-bold text-lg">Address Updated!</p>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Aapka delivery address save ho gaya</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="overflow-y-auto flex-1">
            <div className="px-6 py-4 space-y-4">
              {/* Full Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  <i className="ri-user-line mr-1 text-orange-400"></i>Full Name *
                </label>
                <input
                  type="text"
                  value={form.fullName}
                  onChange={e => handleChange('fullName', e.target.value)}
                  placeholder="Apna poora naam daalo"
                  className={`w-full px-4 py-3 rounded-xl border text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-slate-800 placeholder-gray-400 dark:placeholder-gray-500 outline-none transition-all focus:bg-white dark:focus:bg-slate-700 ${errors.fullName ? 'border-red-400 focus:border-red-500' : 'border-gray-200 dark:border-slate-600 focus:border-orange-400'}`}
                />
                {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  <i className="ri-phone-line mr-1 text-orange-400"></i>Phone Number *
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => handleChange('phone', e.target.value)}
                  placeholder="10-digit mobile number"
                  maxLength={13}
                  className={`w-full px-4 py-3 rounded-xl border text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-slate-800 placeholder-gray-400 dark:placeholder-gray-500 outline-none transition-all focus:bg-white dark:focus:bg-slate-700 ${errors.phone ? 'border-red-400 focus:border-red-500' : 'border-gray-200 dark:border-slate-600 focus:border-orange-400'}`}
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>

              {/* Full Address */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  <i className="ri-home-3-line mr-1 text-orange-400"></i>House / Flat / Street *
                </label>
                <textarea
                  value={form.address}
                  onChange={e => handleChange('address', e.target.value)}
                  placeholder="House no., society, street name, area..."
                  rows={2}
                  maxLength={300}
                  className={`w-full px-4 py-3 rounded-xl border text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-slate-800 placeholder-gray-400 dark:placeholder-gray-500 outline-none resize-none transition-all focus:bg-white dark:focus:bg-slate-700 ${errors.address ? 'border-red-400 focus:border-red-500' : 'border-gray-200 dark:border-slate-600 focus:border-orange-400'}`}
                />
                {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
              </div>

              {/* City + Pincode */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    <i className="ri-building-2-line mr-1 text-orange-400"></i>City *
                  </label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={e => handleChange('city', e.target.value)}
                    placeholder="City name"
                    className={`w-full px-3 py-3 rounded-xl border text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-slate-800 placeholder-gray-400 dark:placeholder-gray-500 outline-none transition-all focus:bg-white dark:focus:bg-slate-700 ${errors.city ? 'border-red-400 focus:border-red-500' : 'border-gray-200 dark:border-slate-600 focus:border-orange-400'}`}
                  />
                  {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    <i className="ri-map-2-line mr-1 text-orange-400"></i>Pincode *
                  </label>
                  <input
                    type="text"
                    value={form.pincode}
                    onChange={e => handleChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="6-digit pincode"
                    maxLength={6}
                    className={`w-full px-3 py-3 rounded-xl border text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-slate-800 placeholder-gray-400 dark:placeholder-gray-500 outline-none transition-all focus:bg-white dark:focus:bg-slate-700 ${errors.pincode ? 'border-red-400 focus:border-red-500' : 'border-gray-200 dark:border-slate-600 focus:border-orange-400'}`}
                  />
                  {errors.pincode && <p className="text-red-500 text-xs mt-1">{errors.pincode}</p>}
                </div>
              </div>

              {/* GPS Location */}
              <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-3 border border-orange-100 dark:border-orange-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900/40">
                      <i className={`ri-crosshair-2-line text-orange-500 text-sm ${gettingLocation ? 'animate-spin' : ''}`}></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">GPS Location</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">
                        {form.lat && form.lng ? `${form.lat.toFixed(4)}, ${form.lng.toFixed(4)}` : 'Location not captured'}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleGetLocation}
                    disabled={gettingLocation}
                    className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {gettingLocation ? 'Getting...' : form.lat ? 'Update' : 'Get GPS'}
                  </button>
                </div>
                {locationStatus && (
                  <p className={`text-xs mt-2 font-medium ${locationStatus.includes('captured') ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                    <i className={`mr-1 ${locationStatus.includes('captured') ? 'ri-checkbox-circle-line' : 'ri-information-line'}`}></i>
                    {locationStatus}
                  </p>
                )}
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="px-6 pb-6 pt-2 flex gap-3 shrink-0 border-t border-gray-100 dark:border-slate-800">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3.5 rounded-xl border-2 border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-300 font-semibold text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-3.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-save-3-line"></i>
                    Save Address
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
